import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type { BackendTask } from '@modules/background-tasks';
import { BaseDialog, DialogContent, DialogFooter, DialogHeader } from '@modules/ui-components';
import { UnifiedPostDialog } from '@modules/content-plan/components/postLike/UnifiedPostDialog';
import { useTaskPostArtifacts, type ResolvedTaskPostArtifact } from '@modules/content-plan/hooks/useTaskPostArtifacts';
import {
  hasPublicationPostArtifactFallback,
  normalizeTaskPostArtifactRefs,
} from '@modules/content-plan/utils/taskPostArtifacts';

function resultPostCount(task: BackendTask): number {
  const result = task.result as Record<string, unknown> | undefined;
  return typeof result?.postCount === 'number' ? result.postCount : 0;
}

function artifactLabel(count: number): string {
  return count === 1 ? 'Show generated post' : 'Show generated posts';
}

function rowPreview(artifact: ResolvedTaskPostArtifact): string {
  return artifact.ref.previewText
    ?? artifact.post?.title
    ?? artifact.post?.hook
    ?? artifact.post?.body
    ?? artifact.ref.postId;
}

export interface TaskPostArtifactsActionProps {
  task: BackendTask;
  variant?: 'button' | 'icon';
}

const TaskPostArtifactsDialog: React.FC<{
  task: BackendTask;
  open: boolean;
  onClose: () => void;
}> = ({ task, open, onClose }) => {
  const navigate = useNavigate();
  const { supported, artifacts, loading, error } = useTaskPostArtifacts(task);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (artifacts.length === 1 && artifacts[0].post) {
      setSelectedId(artifacts[0].ref.postId);
    }
  }, [open, artifacts]);

  const selected = useMemo(
    () => artifacts.find((artifact) => artifact.ref.postId === selectedId) ?? null,
    [artifacts, selectedId],
  );

  if (!supported) return null;

  const openEditableLocation = (artifact?: ResolvedTaskPostArtifact | null) => {
    const url = artifact?.ref.editableUrl ?? task.targetEntityPath?.frontendUrl;
    if (url) {
      navigate(url);
      onClose();
      setSelectedId(null);
    }
  };

  const closePreview = () => {
    if (artifacts.length > 1) {
      setSelectedId(null);
      return;
    }
    onClose();
    setSelectedId(null);
  };

  const showPicker = open && !selected;
  const showPreview = open && selected?.post;

  return (
    <>
      <BaseDialog
        open={showPicker}
        onClose={() => {
          onClose();
          setSelectedId(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogHeader
          title="Generated posts from this task"
          subtitle="Task artifacts are read-only here. Open the editable location to make changes."
          onClose={() => {
            onClose();
            setSelectedId(null);
          }}
        />
        <DialogContent>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error.message}</Alert>}
            {loading && artifacts.length === 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Loading generated posts...</Typography>
              </Stack>
            )}
            {!loading && artifacts.length === 0 && (
              <Alert
                severity="info"
                action={
                  task.targetEntityPath?.frontendUrl ? (
                    <Button size="small" onClick={() => openEditableLocation()}>
                      Open related item
                    </Button>
                  ) : undefined
                }
              >
                This task does not expose generated post artifacts. Open the related topic to inspect results.
              </Alert>
            )}
            {artifacts.length > 0 && (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Platform</TableCell>
                      <TableCell>Format</TableCell>
                      <TableCell>Storage</TableCell>
                      <TableCell>Preview</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {artifacts.map((artifact) => (
                      <TableRow key={artifact.ref.postId} hover>
                        <TableCell>{artifact.ref.platform ?? '—'}</TableCell>
                        <TableCell>{artifact.ref.format ?? '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={artifact.ref.storage === 'content_posts' ? 'Canonical' : 'Draft'}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          <Typography variant="body2" noWrap title={rowPreview(artifact)}>
                            {rowPreview(artifact)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon />}
                              disabled={!artifact.post}
                              onClick={() => setSelectedId(artifact.ref.postId)}
                            >
                              Preview
                            </Button>
                            <Button
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              onClick={() => openEditableLocation(artifact)}
                            >
                              Open editable location
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogFooter
          secondaryAction={{
            label: 'Close',
            onClick: () => {
              onClose();
              setSelectedId(null);
            },
          }}
        />
      </BaseDialog>

      {showPreview && (
        <UnifiedPostDialog
          open={Boolean(showPreview)}
          onClose={closePreview}
          post={selected.post}
          live={selected.post}
          readOnly
          readOnlyAdditionalActions={[
            {
              label: 'Open editable location',
              onClick: () => openEditableLocation(selected),
              variant: 'outlined',
              startIcon: <OpenInNewIcon />,
            },
          ]}
          onSave={async () => selected.post?.id ?? selected.ref.postId}
        />
      )}
    </>
  );
};

export const TaskPostArtifactsAction: React.FC<TaskPostArtifactsActionProps> = ({ task, variant = 'button' }) => {
  const [open, setOpen] = useState(false);
  const supported = String(task.taskType ?? '') === 'content-post-generation';
  const explicitCount = normalizeTaskPostArtifactRefs(task).length;
  const displayCount = explicitCount || resultPostCount(task) || (hasPublicationPostArtifactFallback(task) ? 1 : 0);

  if (!supported || displayCount === 0) return null;

  return (
    <>
      {variant === 'icon' ? (
        <Tooltip title={artifactLabel(displayCount)}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            data-testid={`task-generated-post-artifacts-${task.id}`}
          >
            <ArticleIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArticleIcon />}
              onClick={() => setOpen(true)}
              data-testid="task-generated-post-artifacts-button"
            >
              {artifactLabel(displayCount)}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {displayCount} artifact{displayCount === 1 ? '' : 's'} from this task
            </Typography>
          </Stack>
        </Box>
      )}
      {open && (
        <TaskPostArtifactsDialog
          task={task}
          open={open}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default TaskPostArtifactsAction;
